(() => {
  'use strict';
  if (window.ReglageWatchCarousels) return;
  const mobile = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new Map();
  const homeIntroKey = 'reglage.home-carousel.intro.v1';
  let homeIntroConsumed = false;
  const interpolate = (text, values) => text.replace(/__(NAME|CURRENT|TOTAL)__|\{(name|current|total)\}/g, (_, token, key) => values[key || token.toLowerCase()]);
  const isGeneralEntry = () => {
    const parameters = new URLSearchParams(window.location.search);
    return parameters.get('entry') === 'general' && !parameters.has('model');
  };

  function createRail(root, models, select, signal) {
    if (!root) return { render() {}, destroy() {} };
    const input = root.querySelector('input');
    const marker = root.querySelector('[data-reglage-carousel-marker]');
    const limit = models.length - 1;
    let travel = 0;
    let current = { position: 0, velocity: 0, index: 0, moving: false };
    function render(position, velocity, index, moving) {
      current = { position, velocity, index, moving };
      const fraction = limit > 0 ? Math.max(0, Math.min(limit, position)) / limit : 0;
      const stretch = reducedMotion.matches ? 1 : 1 + Math.min(.65, Math.abs(velocity) * .065);
      marker.style.transform = 'translate3d(' + fraction * travel + 'px, 0, 0) scaleX(' + stretch + ')';
      root.classList.toggle('is-moving', moving);
      input.value = String(index);
      input.setAttribute('aria-valuetext', models[index].name);
    }
    function measure() {
      travel = Math.max(0, root.clientWidth - 24);
      render(current.position, current.velocity, current.index, current.moving);
    }
    input.addEventListener('input', () => select(Number(input.value)), { signal });
    input.addEventListener('keydown', event => {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) event.stopPropagation();
    }, { signal });
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    measure();
    return { render, destroy: () => observer.disconnect() };
  }

  function createDepthCarousel(root, config, settings) {
    const models = config.models;
    const limit = models.length - 1;
    const clamp = (value, minimum = 0, maximum = limit) => Math.max(minimum, Math.min(maximum, value));
    const cards = Array.from(root.querySelectorAll('[data-reglage-selector-watch]'));
    const scene = root.querySelector('[data-reglage-selector-scene]');
    const previous = root.querySelector('[data-reglage-selector-previous]');
    const next = root.querySelector('[data-reglage-selector-next]');
    const continueLink = root.querySelector('[data-reglage-selector-continue]');
    const modelName = root.querySelector('[data-reglage-selector-name]');
    const price = root.querySelector('[data-reglage-selector-price]');
    const counter = root.querySelector('[data-reglage-selector-position]');
    const family = root.querySelector('[data-reglage-selector-family]');
    const announcement = root.querySelector('[data-reglage-selector-announcement]');
    const formatter = new Intl.NumberFormat(config.locale || 'en', { style: 'currency', currency: config.currency || 'USD' });
    const strings = config.selectorStrings;
    const abort = new AbortController();
    const options = { signal: abort.signal };
    let target = Math.round(clamp(settings.readIndex()));
    let position = target;
    let velocity = 0;
    let selected = -1;
    let width = 1;
    let height = 1;
    let frame = 0;
    let lastFrameTime = 0;
    let drag = null;
    let suppressClickUntil = 0;
    let focusOnSettlement = false;
    let wheelTimer = 0;
    let wheelTime = 0;
    const rail = createRail(root.querySelector('[data-reglage-carousel-rail]'), models, index => select(index), abort.signal);

    function updateInformation(index) {
      if (selected === index) return;
      selected = index;
      const model = models[index];
      modelName.textContent = model.name;
      if (price) price.textContent = formatter.format(Number(model.price));
      if (family) family.textContent = String(index + 1).padStart(2, '0') + ' / ' + model.family;
      counter.textContent = interpolate(strings.position, { current: index + 1, total: models.length });
      continueLink.href = settings.destination(model, continueLink);
      previous.disabled = index === 0;
      next.disabled = index === limit;
      cards.forEach((card, cardIndex) => {
        card.setAttribute('aria-pressed', String(cardIndex === index));
        card.tabIndex = cardIndex === index ? 0 : -1;
      });
      settings.onSelection?.(index);
    }
    function render() {
      const moving = !!frame || !!drag?.active || !!wheelTime;
      cards.forEach((card, index) => {
        const offset = index - position;
        const distance = Math.abs(offset);
        const visible = distance < 1.7;
        card.style.visibility = visible ? 'visible' : 'hidden';
        card.setAttribute('aria-hidden', String(!visible));
        card.classList.toggle('is-moving', moving && visible);
        if (!visible) return;
        const depth = Math.min(distance, 2);
        const scale = Math.max(.43, 1 - .333 * depth);
        const opacity = distance <= 1 ? 1 - .28 * distance : Math.max(0, .72 * (1.7 - distance) / .7);
        card.style.opacity = String(opacity);
        card.style.transform = 'translate(-50%, -50%) translate3d(' + offset * width * .3136 + 'px, ' + -height * .0823 * Math.min(distance, 1) + 'px, ' + -width * .3077 * depth + 'px) rotateY(' + -18 * clamp(offset, -1, 1) + 'deg) scale(' + scale + ')';
        card.style.zIndex = String(10 - Math.round(distance * 2));
        card.style.pointerEvents = distance < 1.6 ? 'auto' : 'none';
      });
      rail.render(position, drag?.active ? drag.speed : velocity, selected, moving);
    }
    function announce() {
      announcement.textContent = interpolate(strings.announcement, { name: models[target].name, current: target + 1, total: models.length });
      if (focusOnSettlement) { cards[target].focus({ preventScroll: true }); focusOnSettlement = false; }
    }
    function stopFrame() {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      lastFrameTime = 0;
    }
    function tick(now) {
      const seconds = lastFrameTime ? Math.min((now - lastFrameTime) / 1000, .05) : 1 / 60;
      lastFrameTime = now;
      const displacement = position - target;
      const momentum = velocity + 14 * displacement;
      const decay = Math.exp(-14 * seconds);
      position = target + (displacement + momentum * seconds) * decay;
      velocity = (velocity - 14 * momentum * seconds) * decay;
      if (Math.abs(position - target) < .0003 && Math.abs(velocity) < .005) {
        position = target;
        velocity = 0;
        frame = 0;
        lastFrameTime = 0;
        render();
        announce();
        return;
      }
      render();
      frame = window.requestAnimationFrame(tick);
    }
    function select(index, focus = false, automatic = false) {
      if (!automatic) settings.onInteraction?.();
      target = Math.round(clamp(index));
      updateInformation(target);
      settings.persist?.(target);
      if (focus) { focusOnSettlement = true; root.focus({ preventScroll: true }); }
      if (reducedMotion.matches) {
        stopFrame();
        position = target;
        velocity = 0;
        render();
        announce();
      } else if (!frame) {
        lastFrameTime = 0;
        frame = window.requestAnimationFrame(tick);
        render();
      }
    }
    function measure() {
      width = scene.clientWidth || 1;
      height = scene.clientHeight || 1;
      render();
    }
    function pointerDown(event) {
      if (!mobile.matches || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (event.target.closest('[data-reglage-selector-previous], [data-reglage-selector-next], a, input')) return;
      clearTimeout(wheelTimer);
      wheelTime = 0;
      drag = { id: event.pointerId, surface: event.currentTarget, x: event.clientX, y: event.clientY, start: position, lastX: event.clientX, time: event.timeStamp, speed: 0, active: false };
    }
    function pointerMove(event) {
      if (!drag || event.pointerId !== drag.id) return;
      let dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
        if (Math.abs(dx) < 6 || Math.abs(dx) <= Math.abs(dy)) return;
        drag.active = true;
        settings.onInteraction?.();
        drag.start = position;
        drag.x = event.clientX;
        dx = 0;
        stopFrame();
        drag.surface.setPointerCapture(event.pointerId);
      }
      if (event.cancelable) event.preventDefault();
      const elapsed = Math.max(1, event.timeStamp - drag.time);
      const sample = -(event.clientX - drag.lastX) / (width * .3136) / (elapsed / 1000);
      drag.speed = .65 * sample + .35 * drag.speed;
      drag.lastX = event.clientX;
      drag.time = event.timeStamp;
      const raw = drag.start - dx / (width * .3136);
      position = raw < 0 ? raw * .22 : raw > limit ? limit + (raw - limit) * .22 : raw;
      velocity = 0;
      updateInformation(Math.round(clamp(position)));
      render();
    }
    function pointerEnd(event) {
      if (!drag || drag.id !== event.pointerId) return;
      const ended = drag;
      drag = null;
      if (!ended.active) return;
      if (ended.surface.hasPointerCapture(event.pointerId)) ended.surface.releasePointerCapture(event.pointerId);
      suppressClickUntil = performance.now() + 350;
      const speed = event.type === 'pointercancel' || event.timeStamp - ended.time > 100 ? 0 : clamp(ended.speed, -5, 5);
      velocity = speed;
      select(position + speed * .16);
    }
    previous.addEventListener('click', () => select(target - 1), options);
    next.addEventListener('click', () => select(target + 1), options);
    cards.forEach((card, index) => card.addEventListener('click', () => select(index), options));
    root.addEventListener('click', event => {
      if (performance.now() < suppressClickUntil && event.target.closest('[data-reglage-selector-watch]')) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, { capture: true, signal: abort.signal });
    root.querySelectorAll('[data-reglage-selector-swipe]').forEach(surface => {
      surface.addEventListener('pointerdown', pointerDown, options);
      surface.addEventListener('pointermove', pointerMove, options);
      surface.addEventListener('pointerup', pointerEnd, options);
      surface.addEventListener('pointercancel', pointerEnd, options);
      surface.addEventListener('wheel', event => {
        const dx = event.deltaMode === 1 ? event.deltaX * 16 : event.deltaMode === 2 ? event.deltaX * width : event.deltaX;
        if (!mobile.matches || Math.abs(dx) <= Math.abs(event.deltaY)) return;
        settings.onInteraction?.();
        event.preventDefault();
        stopFrame();
        const distance = dx / (width * .3136);
        const elapsed = wheelTime ? Math.max(8, event.timeStamp - wheelTime) : 16;
        wheelTime = event.timeStamp;
        velocity = clamp(distance / (elapsed / 1000), -5, 5);
        position = clamp(position + distance);
        updateInformation(Math.round(position));
        render();
        clearTimeout(wheelTimer);
        wheelTimer = setTimeout(() => { wheelTime = 0; select(position + velocity * .08); }, 100);
      }, { passive: false, signal: abort.signal });
    });
    root.addEventListener('keydown', event => {
      if (event.target.closest('a, input')) return;
      const actions = { ArrowLeft: target - 1, ArrowRight: target + 1, Home: 0, End: limit };
      if (!(event.key in actions)) return;
      event.preventDefault();
      select(actions[event.key], true);
    }, options);
    function suspend() {
      clearTimeout(wheelTimer);
      wheelTime = 0;
      if (drag?.active && drag.surface.hasPointerCapture(drag.id)) drag.surface.releasePointerCapture(drag.id);
      drag = null;
      stopFrame();
    }
    function restore(event) {
      if (settings.beforeRestore?.(event) === false) return;
      suspend();
      focusOnSettlement = false;
      target = Math.round(clamp(settings.readIndex()));
      position = target;
      velocity = 0;
      updateInformation(target);
      measure();
    }
    const observer = new ResizeObserver(measure);
    observer.observe(scene);
    window.addEventListener('popstate', restore, options);
    window.addEventListener('pageshow', restore, options);
    window.addEventListener('pagehide', suspend, options);
    document.addEventListener('visibilitychange', () => { if (document.hidden) suspend(); else restore(); }, options);
    reducedMotion.addEventListener('change', restore, options);
    mobile.addEventListener('change', restore, options);
    updateInformation(target);
    measure();
    return {
      selectAutomatically(index) { select(index, false, true); },
      destroy() { suspend(); observer.disconnect(); rail.destroy(); abort.abort(); }
    };
  }

  function createDesktopCarousel(root, config, state, settings = {}) {
    const models = config.models;
    const limit = models.length - 1;
    const clamp = value => Math.max(0, Math.min(limit, value));
    const track = root.querySelector('[data-configurator-carousel-track]');
    const cards = Array.from(track.querySelectorAll('[data-configurator-carousel-slide]'));
    const previous = root.querySelector('[data-configurator-carousel-prev]');
    const next = root.querySelector('[data-configurator-carousel-next]');
    const count = root.querySelector('[data-configurator-carousel-count]');
    const abort = new AbortController();
    const options = { signal: abort.signal };
    let centers = [];
    let stride = 1;
    let target = state.index;
    let selected = target;
    let velocity = 0;
    let animatedPosition = track.scrollLeft;
    let frame = 0;
    let scrollFrame = 0;
    let resizeFrame = 0;
    let settleTimer = 0;
    let lastTime = 0;
    let automaticAnimation = false;
    const railRoot = root.querySelector('[data-reglage-carousel-desktop-rail] [data-reglage-carousel-rail]');
    const rail = createRail(railRoot, models, index => select(index), abort.signal);
    const position = () => clamp((track.scrollLeft - (centers[0] || 0)) / stride);
    function stopFrame() { if (frame) window.cancelAnimationFrame(frame); frame = 0; lastTime = 0; automaticAnimation = false; }
    function paint() {
      if (mobile.matches || !centers.length) return;
      const current = position();
      selected = Math.round(current);
      cards.forEach((card, index) => {
        const distance = Math.abs(index - current);
        const scale = distance <= 1 ? 1 - .14 * distance : Math.max(.72, .86 - .14 * (distance - 1));
        const opacity = distance <= 1 ? 1 - .32 * distance : Math.max(.34, .68 - .34 * (distance - 1));
        card.style.transform = 'scale(' + scale + ')';
        card.style.opacity = String(opacity);
        card.classList.toggle('is-active', index === selected);
        card.classList.toggle('is-neighbor', Math.abs(index - selected) === 1);
        card.classList.toggle('is-moving', !!frame && distance < 3);
        if (index === selected) card.setAttribute('aria-current', 'true'); else card.removeAttribute('aria-current');
      });
      previous.disabled = selected === 0;
      next.disabled = selected === limit;
      count.textContent = String(selected + 1).padStart(2, '0') + ' / ' + String(models.length).padStart(2, '0');
      if (!frame) state.index = selected;
      rail.render(current, velocity / stride, frame ? target : selected, !!frame);
    }
    function tick(now) {
      const seconds = lastTime ? Math.min((now - lastTime) / 1000, .05) : 1 / 60;
      lastTime = now;
      // Keep the spring in floating-point space: some browsers round scrollLeft.
      const displacement = animatedPosition - centers[target];
      const momentum = velocity + 14 * displacement;
      const decay = Math.exp(-14 * seconds);
      animatedPosition = centers[target] + (displacement + momentum * seconds) * decay;
      track.scrollLeft = animatedPosition;
      velocity = (velocity - 14 * momentum * seconds) * decay;
      if (Math.abs(animatedPosition - centers[target]) < .5 && Math.abs(velocity) < 4) {
        animatedPosition = centers[target];
        track.scrollLeft = centers[target];
        velocity = 0;
        stopFrame();
        paint();
        return;
      }
      paint();
      frame = window.requestAnimationFrame(tick);
    }
    function select(index, automatic = false) {
      if (mobile.matches || !centers.length) return;
      if (!automatic) settings.onInteraction?.();
      automaticAnimation = automatic;
      clearTimeout(settleTimer);
      target = Math.round(clamp(index));
      state.index = target;
      if (reducedMotion.matches) { stopFrame(); track.scrollLeft = centers[target]; velocity = 0; paint(); }
      else if (!frame) { animatedPosition = track.scrollLeft; lastTime = 0; frame = window.requestAnimationFrame(tick); }
    }
    function suspend() {
      stopFrame();
      window.cancelAnimationFrame(scrollFrame);
      window.cancelAnimationFrame(resizeFrame);
      clearTimeout(settleTimer);
      cards.forEach(card => card.classList.remove('is-moving'));
    }
    function measure() {
      suspend();
      if (mobile.matches) return;
      target = Math.round(clamp(state.index));
      const trackBounds = track.getBoundingClientRect();
      centers = cards.map(card => {
        const bounds = card.getBoundingClientRect();
        return track.scrollLeft + bounds.left + bounds.width / 2 - trackBounds.left - trackBounds.width / 2;
      });
      stride = centers.length > 1 ? centers[1] - centers[0] : 1;
      animatedPosition = centers[target];
      track.scrollLeft = centers[target];
      velocity = 0;
      paint();
    }
    const interrupt = event => {
      // Vertical page scrolling should carry the entrance, not stop it midway.
      const verticalWheel = event.type === 'wheel' && Math.abs(event.deltaX) <= Math.abs(event.deltaY);
      if (automaticAnimation && verticalWheel) return;
      if (!verticalWheel) settings.onInteraction?.();
      stopFrame(); animatedPosition = track.scrollLeft; velocity = 0; target = Math.round(position()); state.index = target;
    };
    previous.addEventListener('click', () => select(target - 1), options);
    next.addEventListener('click', () => select(target + 1), options);
    cards.forEach((card, index) => card.addEventListener('focus', () => select(index), options));
    track.addEventListener('pointerdown', interrupt, { passive: true, signal: abort.signal });
    track.addEventListener('wheel', interrupt, { passive: true, signal: abort.signal });
    track.addEventListener('scroll', () => {
      if (frame || mobile.matches) return;
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(paint);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => { target = selected; select(selected); }, 110);
    }, { passive: true, signal: abort.signal });
    track.addEventListener('keydown', event => {
      const actions = { ArrowLeft: target - 1, ArrowRight: target + 1, Home: 0, End: limit };
      if (!(event.key in actions)) return;
      event.preventDefault();
      select(actions[event.key]);
    }, options);
    const observer = new ResizeObserver(() => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(measure);
    });
    observer.observe(track);
    reducedMotion.addEventListener('change', measure, options);
    mobile.addEventListener('change', measure, options);
    window.addEventListener('pagehide', suspend, options);
    window.addEventListener('pageshow', measure, options);
    document.addEventListener('visibilitychange', () => { if (document.hidden) suspend(); else measure(); }, options);
    measure();
    return {
      selectAutomatically(index) { select(index, true); },
      destroy() { suspend(); observer.disconnect(); rail.destroy(); abort.abort(); }
    };
  }

  function shouldPlayHomeIntro(config) {
    if (!document.body.classList.contains('reglage-template-index') || window.Shopify?.designMode || homeIntroConsumed) return false;
    if (config.models[0]?.slug !== 'new-master-2' || config.models[1]?.slug !== 'new-datejust-2') return false;
    try { return window.localStorage.getItem(homeIntroKey) !== 'seen'; }
    catch { return true; }
  }

  function consumeHomeIntro() {
    homeIntroConsumed = true;
    try { window.localStorage.setItem(homeIntroKey, 'seen'); }
    catch { /* With unavailable storage, the entrance still runs only once on this page. */ }
  }

  function createHomeIntro(root, advance) {
    const abort = new AbortController();
    const options = { signal: abort.signal };
    let pending = true;
    let frame = 0;
    let previousY = window.scrollY;
    let downward = false;
    function suspend() {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      previousY = window.scrollY;
      downward = false;
    }
    function finish(consume) {
      if (!pending) return;
      pending = false;
      suspend();
      abort.abort();
      if (consume) consumeHomeIntro();
    }
    function checkEntrance() {
      frame = 0;
      if (!pending || !downward || document.hidden) return;
      const scene = root.querySelector(mobile.matches ? '[data-reglage-carousel-mobile] [data-reglage-selector-scene]' : '.configurator-carousel-stage');
      if (!scene) return;
      const bounds = scene.getBoundingClientRect();
      // Let the first watch become visible before carrying the focus to DateJust.
      if (bounds.height > 0 && bounds.bottom > 0 && bounds.top + bounds.height / 2 <= window.innerHeight * .65) {
        finish(true);
        advance();
      }
    }
    function onScroll() {
      const y = window.scrollY;
      downward = y > previousY;
      previousY = y;
      if (downward && !frame) frame = window.requestAnimationFrame(checkEntrance);
    }
    function explicitControl(event) {
      if (event.target.closest('a, input, [data-reglage-selector-previous], [data-reglage-selector-next], [data-configurator-carousel-prev], [data-configurator-carousel-next]')) finish(true);
    }
    window.addEventListener('scroll', onScroll, { passive: true, signal: abort.signal });
    window.addEventListener('pagehide', suspend, options);
    window.addEventListener('pageshow', suspend, options);
    document.addEventListener('visibilitychange', suspend, options);
    root.addEventListener('pointerdown', explicitControl, { capture: true, signal: abort.signal });
    root.addEventListener('focusin', explicitControl, options);
    root.addEventListener('click', explicitControl, { capture: true, signal: abort.signal });
    return { cancel() { finish(true); }, destroy() { finish(false); } };
  }

  function initializeSelector(root) {
    if (!isGeneralEntry() || controllers.has(root)) return;
    if (!mobile.matches) { window.location.replace('/pages/configurators'); return; }
    const dataRoot = document.getElementById(root.dataset.reglageConfiguratorRoot);
    const config = JSON.parse(dataRoot.querySelector('[data-reglage-configurator-data]').textContent);
    const cards = Array.from(root.querySelectorAll('[data-reglage-selector-watch]'));
    const models = cards.map(card => config.models.find(model => model.slug === card.dataset.reglageSelectorSlug));
    if (!models.length || models.some(model => !model)) throw new Error('Watch model inventory is unavailable.');
    // Every entry starts on DateJust; only this open page retains manual choices.
    const initialIndex = Math.max(0, models.findIndex(model => model.slug === 'new-datejust-2'));
    const state = { index: initialIndex };
    const controller = createDepthCarousel(root, { ...config, models }, {
      readIndex: () => state.index,
      destination(model, link) {
        const url = new URL(link.href, window.location.href);
        url.search = '';
        url.searchParams.set('model', model.slug);
        return url.pathname + url.search;
      },
      persist(index) {
        state.index = index;
      },
      beforeRestore(event) {
        if (!mobile.matches && isGeneralEntry()) { window.location.replace('/pages/configurators'); return false; }
        if (event?.type === 'pageshow') state.index = initialIndex;
        return true;
      }
    });
    controllers.set(root, controller);
    document.documentElement.classList.add('reglage-general-entry');
  }

  function initializeHome(root) {
    if (controllers.has(root)) return;
    const config = JSON.parse(root.querySelector('[data-reglage-carousel-data]').textContent);
    if (!config.models.length) return;
    const playIntro = shouldPlayHomeIntro(config);
    const state = { index: playIntro ? 0 : Math.max(0, Math.min(config.models.length - 1, Number(config.initialIndex) || 0)) };
    const mobileRoot = root.querySelector('[data-reglage-carousel-mobile]');
    root.classList.add('reglage-motion-home', 'is-motion-enhanced');
    let depth;
    let desktop;
    let intro;
    const onInteraction = () => intro?.cancel();
    try {
      depth = createDepthCarousel(mobileRoot, config, {
        readIndex: () => state.index,
        destination: model => model.href,
        onInteraction,
        onSelection(index) {
          if (!mobile.matches) return;
          state.index = index;
          root.querySelector('[data-configurator-carousel-count]').textContent = String(index + 1).padStart(2, '0') + ' / ' + String(config.models.length).padStart(2, '0');
        }
      });
      desktop = createDesktopCarousel(root, config, state, { onInteraction });
      if (playIntro) intro = createHomeIntro(root, () => {
        (mobile.matches ? depth : desktop).selectAutomatically(1);
      });
    } catch (error) {
      intro?.destroy();
      depth?.destroy();
      desktop?.destroy();
      root.classList.remove('is-motion-enhanced');
      throw error;
    }
    controllers.set(root, { destroy() { intro?.destroy(); depth.destroy(); desktop.destroy(); root.classList.remove('is-motion-enhanced'); } });
  }

  function initializeAll(container = document) {
    container.querySelectorAll('[data-reglage-model-selector]').forEach(root => {
      try { initializeSelector(root); }
      catch (error) { console.error('[Reglage model selector]', error); window.location.replace('/pages/configurators'); }
    });
    container.querySelectorAll('[data-reglage-carousel-motion]').forEach(root => {
      try { initializeHome(root); }
      catch (error) { console.error('[Reglage homepage carousel]', error); }
    });
  }
  window.ReglageWatchCarousels = { initialize: initializeAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', event => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-reglage-model-selector], [data-reglage-carousel-motion]').forEach(root => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });
  });
})();
